import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LogOut, 
  BarChart3, 
  Settings, 
  Home, 
  Users, 
  ChevronDown, 
  ChevronUp,
  Menu,
  X,
  Briefcase,
  Image,
  Gift,
  FileText
} from 'lucide-react';
import api from '../../utils/axios';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Keyboard shortcut to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setSidebarOpen(!sidebarOpen);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  const handleLogout = () => {
    setLogoutLoading(true);
    api.post('/admin/logout', {})
      .then(() => navigate('/admin/login'))
      .catch(() => console.error('Logout failed'))
      .finally(() => setLogoutLoading(false));
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: Home },
    { name: 'Enquiries', href: '/admin/dashboard', icon: Users },
    { name: 'Franchise Enquiries', href: '/admin/franchise-enquiries', icon: Users },
    { name: 'Hiring Requests', href: '/admin/hiring-requests', icon: Briefcase },
    { name: 'Contact Submissions', href: '/admin/contacts', icon: Users },
    { name: 'Workshop Registrations', href: '/admin/workshop-registrations', icon: Users },
    { name: 'Placement Resumes', href: '/admin/placement-resumes', icon: FileText },
    { name: 'Promotional Slides', href: '/admin/promotional-slides', icon: Image },
    { name: 'Franchise Hero Images', href: '/admin/franchise-hero-images', icon: Image },
    { name: 'Coupons', href: '/admin/coupons', icon: Gift },
    { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const Sidebar = () => (
    <div className={`bg-white dark:bg-gray-900 shadow-xl border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-30 h-screen flex flex-col ${sidebarOpen ? 'w-72' : 'w-16'} fixed lg:static`} style={{ minHeight: '100vh', maxHeight: '100vh' }}>
      {/* Header - Fixed at top */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 mt-24 flex-shrink-0">
        <div className="flex items-center min-w-0">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          {sidebarOpen && <span className="text-lg font-bold text-gray-900 dark:text-white truncate">OSOP Admin</span>}
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title={`${sidebarOpen ? 'Collapse' : 'Expand'} Sidebar (Ctrl+B)`}
        >
          {sidebarOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
        </button>
      </div>
      
      {/* Navigation - Scrollable */}
      <nav className="p-4 pb-6 space-y-2 flex-1 overflow-y-auto admin-sidebar-scroll">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          const Icon = item.icon;
          return (
            <button
              key={item.name}
              onClick={() => navigate(item.href)}
              className={`w-full flex items-center p-3 rounded-xl transition-colors ${
                isActive
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
              } ${sidebarOpen ? 'justify-start' : 'justify-center'}`}
              title={!sidebarOpen ? item.name : ''}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="ml-3 font-medium text-sm leading-tight">{item.name}</span>}
            </button>
          );
        })}
      </nav>
      
      {/* Footer - Fixed at bottom */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button
          onClick={handleLogout}
          className={`w-full flex items-center p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors ${sidebarOpen ? 'justify-start' : 'justify-center'}`}
          disabled={logoutLoading}
          title={!sidebarOpen ? 'Logout' : ''}
        >
          <LogOut className="w-5 h-5" />
          {sidebarOpen && <span className="ml-3 font-medium">{logoutLoading ? 'Logging out...' : 'Logout'}</span>}
        </button>
      </div>
      
      {/* Collapsed indicator */}
      {!sidebarOpen && (
        <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-2 h-16 bg-primary-500 rounded-l-full opacity-60"></div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      <Sidebar />
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-16'} lg:ml-0`}>
        <div className="p-6 mt-20 sm:mt-24 lg:mt-28 w-full max-w-none">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">OSOP Admin</h1>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Floating expand button when sidebar is collapsed */}
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="fixed top-4 left-4 z-40 p-2 bg-primary-500 text-white rounded-lg shadow-lg hover:bg-primary-600 transition-colors lg:hidden"
              title="Expand Sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout; 